import { useEffect } from 'react';
import { UseFormClearErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { Checkbox, Typography } from '@vp/swan';
import { __ } from '@99designs/i18n';
import { useFieldViewSkinContext } from '../../../../context/FieldViewContext';
import { useBriefFormContext } from '../../../BriefContext/BriefFormContext';
import { FormInput } from '../../BriefForm';
import { BooleanFieldFragment } from '../../brief.generated';
import { useUpdateBooleanField } from './useUpdateBooleanField';

type BooleanFieldProps = BooleanFieldFragment & {
    register: UseFormRegister<FormInput>;
    setValue: UseFormSetValue<FormInput>;
    clearErrors: UseFormClearErrors<FormInput>;
};

export function Input({
    id,
    required,
    register,
    booleanValue,
    toggleLabel,
    setValue,
    clearErrors,
    errorMessage,
}: BooleanFieldProps) {
    const { briefId } = useBriefFormContext();
    const updateMutation = useUpdateBooleanField(id, briefId, booleanValue);
    const errorMsg = errorMessage
        ? errorMessage
        : __('This field is required', {
              _context: 'Error message displayed when a required form field is not filled out',
          });

    useEffect(() => {
        setValue(id, booleanValue);
    }, []);

    return (
        <label>
            <Checkbox
                data-testid={id}
                defaultChecked={booleanValue || false}
                {...register(id, {
                    required: required ? errorMsg : false,
                    onChange: (e) => {
                        setValue(id, e.target.checked);
                        updateMutation(e.target.checked);
                        clearErrors(id);
                    },
                })}
                aria-required={required ? true : undefined}
            />
            {toggleLabel}
        </label>
    );
}

export function View({ booleanValue }: { booleanValue: BooleanFieldFragment['booleanValue'] }) {
    const skin = useFieldViewSkinContext();
    return (
        <Typography fontSize={skin.fontSize}>
            {booleanValue
                ? __('Yes', {
                      _context: "Text on a view only page which shows the customer's selection",
                  })
                : __('No', {
                      _context: "Text on a view only page which shows the customer's selection",
                  })}
        </Typography>
    );
}
